// src/components/Navigation/Footer.tsx - Updated with proper internal links integration
import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Shield, Coffee } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext.tsx';
import { BRAND_NAME_TM, TAGLINE, BRAND_CONFIG } from '../../config/branding.ts';
import LazyImage from '../UI/LazyImage.tsx';

interface FooterLink {
  path: string;
  text: string;
}

const Footer: React.FC = () => {
  const { darkMode } = useTheme();

  // Internal links for SEO (moved inside the component)
  const footerLinks: FooterLink[] = [
    { path: '/', text: 'Home' },
    { path: '/about', text: 'About' },
    { path: '/journal', text: 'Journal' },
    { path: '/forge', text: 'The Forge' },
    { path: '/community', text: 'Community' },
    { path: '/pricing', text: 'Pricing' },
    { path: '/download', text: 'Download' },
    { path: '/blog', text: 'Blog' },
    { path: '/support', text: 'Support & Resources' },
    { path: '/contact', text: 'Contact' },
  ];

  // Legal links
  const legalLinks: FooterLink[] = [
    { path: '/privacy', text: 'Privacy Policy' },
    { path: '/terms', text: 'Terms of Service' },
  ];

  return (
    <footer
      className={`border-t transition-colors duration-300 ${
        darkMode ? 'bg-slate-900 border-orange-800/50' : 'bg-white border-orange-200'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
          {/* Brand Section with Your Actual Logo */}
          <div className="text-center md:text-left">
            <div className="flex items-center justify-center md:justify-start space-x-3 mb-4">
              {/* Your actual logo */}
              <LazyImage
                src="/icons/web-app-manifest-512x512.png"
                alt="Life Re:Scripted Logo"
                className="w-8 h-8 object-contain"
                placeholder={
                  <div className="w-8 h-8 bg-orange-200 dark:bg-orange-800 rounded animate-pulse" />
                }
              />
              <div className="flex flex-col">
                <span
                  className={`font-display text-xl font-bold ${darkMode ? 'text-orange-100' : 'text-slate-800'}`}
                >
                  {BRAND_NAME_TM}
                </span>
                <span
                  className={`text-sm font-medium ${
                    darkMode ? 'text-orange-300' : 'text-orange-600'
                  }`}
                >
                  {TAGLINE}
                </span>
              </div>
            </div>
            <p
              className={`text-sm max-w-md mx-auto md:mx-0 ${
                darkMode ? 'text-slate-400' : 'text-slate-600'
              }`}
            >
              {BRAND_CONFIG.mission.shortDescription}
            </p>
          </div>

          {/* Contact Section */}
          <div className="text-center">
            <h3 className={`font-display text-base font-semibold mb-4 ${darkMode ? 'text-orange-300' : 'text-slate-800'}`}>
              Get in Touch
            </h3>
            <div className="space-y-3">
              <a
                href="mailto:hello@liferescripted.net"
                className={`inline-flex items-center space-x-2 text-sm transition-colors hover:underline ${
                  darkMode
                    ? 'text-slate-400 hover:text-orange-400'
                    : 'text-slate-600 hover:text-orange-600'
                }`}
              >
                <Mail className="w-4 h-4" />
                <span>hello@liferescripted.net</span>
              </a>
              <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                Questions, support, or just want to share your story
              </p>
            </div>
          </div>

          {/* Mission Section */}
          <div className="text-center md:text-right">
            <h3 className={`font-display text-base font-semibold mb-4 ${darkMode ? 'text-orange-300' : 'text-slate-800'}`}>
              Our Mission
            </h3>
            <div className="space-y-3">
              <div className="flex flex-col md:items-end space-y-2">
                <Link
                  to="/about"
                  className={`text-sm transition-colors hover:underline ${
                    darkMode
                      ? 'text-slate-400 hover:text-orange-400'
                      : 'text-slate-600 hover:text-orange-600'
                  }`}
                >
                  About
                </Link>
                <Link
                  to="/michael-connell"
                  className={`text-sm transition-colors hover:underline ${
                    darkMode
                      ? 'text-slate-400 hover:text-orange-400'
                      : 'text-slate-600 hover:text-orange-600'
                  }`}
                >
                  Michael Connell
                </Link>
                <Link
                  to="/founders-corner"
                  className={`text-sm transition-colors hover:underline ${
                    darkMode
                      ? 'text-slate-400 hover:text-orange-400'
                      : 'text-slate-600 hover:text-orange-600'
                  }`}
                >
                  Founder&apos;s Corner
                </Link>
              </div>
              <div className="flex items-center justify-center md:justify-end space-x-2 text-sm">
                <Shield className={`w-4 h-4 ${darkMode ? 'text-orange-400' : 'text-orange-600'}`} />
                <span className={darkMode ? 'text-slate-400' : 'text-slate-600'}>
                  {BRAND_CONFIG.legal.privacyStatement}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* NEW: Site Links Section for SEO - This helps Google discover all your pages */}
        <div
          className={`py-6 border-t border-b ${darkMode ? 'border-slate-800' : 'border-slate-200'}`}
        >
          <h3
            className={`font-display text-center text-base font-semibold mb-4 ${
              darkMode ? 'text-orange-300' : 'text-slate-800'
            }`}
          >
            Explore Life Re:Scripted
          </h3>
          <div className="flex flex-wrap justify-center items-center gap-1">
            {footerLinks.map((link, index) => (
              <React.Fragment key={link.path}>
                <Link
                  to={link.path}
                  className={`text-sm transition-colors hover:underline px-2 py-1 ${
                    darkMode
                      ? 'text-slate-400 hover:text-orange-400'
                      : 'text-slate-600 hover:text-orange-600'
                  }`}
                >
                  {link.text}
                </Link>
                {index < footerLinks.length - 1 && (
                  <span
                    className={`mx-1 text-xs ${darkMode ? 'text-slate-600' : 'text-slate-400'}`}
                  >
                    •
                  </span>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Legal Links */}
        <div className={`pt-4 pb-2 text-center`}>
          <div className="flex flex-wrap justify-center items-center gap-1 mb-2">
            {legalLinks.map((link, index) => (
              <React.Fragment key={link.path}>
                <Link
                  to={link.path}
                  className={`text-xs transition-colors hover:underline px-2 py-1 ${
                    darkMode
                      ? 'text-slate-400 hover:text-orange-400'
                      : 'text-slate-500 hover:text-orange-600'
                  }`}
                >
                  {link.text}
                </Link>
                {index < legalLinks.length - 1 && (
                  <span className={`mx-1 text-xs ${darkMode ? 'text-slate-700' : 'text-slate-400'}`}>
                    |
                  </span>
                )}
              </React.Fragment>
            ))}
            <span className={`mx-1 text-xs ${darkMode ? 'text-slate-700' : 'text-slate-400'}`}>
              |
            </span>
            <a
              href="https://ko-fi.com/liferescripted"
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-flex items-center space-x-1 text-xs transition-colors hover:underline px-2 py-1 ${
                darkMode
                  ? 'text-slate-400 hover:text-orange-400'
                  : 'text-slate-500 hover:text-orange-600'
              }`}
              title="Support us on Ko-fi"
            >
              <Coffee className="w-3 h-3" />
              <span>Support Us</span>
            </a>
          </div>
        </div>

        {/* Bottom Bar with Full Brand Lockup */}
        <div className={`text-center`}>
          <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
            {BRAND_CONFIG.legal.fullCopyright}
          </p>
          <p
            className={`text-xs mt-2 font-medium ${
              darkMode ? 'text-orange-400' : 'text-orange-600'
            }`}
          >
            {BRAND_CONFIG.legal.taglineStatement}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
export type { FooterLink };
